import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Timer } from "lucide-react";

export function SectionLabel({ children, className = "" }) {
  return (
    <div className={`font-mono text-xs tracking-[0.3em] text-white/50 uppercase flex items-center gap-3 ${className}`}>
      <span className="text-white/80">//</span>
      {children}
    </div>
  );
}

export function RevealText({ lines, className = "", delay = 0 }) {
  return (
    <div className={className}>
      {lines.map((line, i) => (
        <div key={i} className="overflow-hidden">
          <motion.div
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: delay + i * 0.12 }}
          >
            {line}
          </motion.div>
        </div>
      ))}
    </div>
  );
}

export function Reveal({ children, delay = 0, y = 40, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay }}
    >
      {children}
    </motion.div>
  );
}

function StatusRadar({ status }) {
  return (
    <span className={`status-radar radar-${status}`} aria-hidden="true">
      <i className="sr-ring r1" />
      <i className="sr-ring r2" />
      <i className="sr-sweep" />
      <i className="sr-core" />
    </span>
  );
}

export function StatusBadge({ status = "undetected", small }) {
  const map = {
    undetected: { label: "UNDETECTED", text: "text-emerald-200", border: "border-emerald-300/30" },
    testing: { label: "TESTING", text: "text-sky-200", border: "border-sky-300/30" },
    updating: { label: "UPDATING", text: "text-amber-200", border: "border-amber-300/30" },
    down: { label: "DOWN", text: "text-red-300", border: "border-red-400/30" },
  };
  const s = map[status] || map.undetected;
  return (
    <span className={`inline-flex items-center gap-2 border ${s.border} bg-black/45 px-2.5 py-1 rounded-full font-mono ${small ? "text-[10px]" : "text-xs"} tracking-[0.15em] ${s.text}`}>
      <StatusRadar status={status} />
      {s.label}
    </span>
  );
}

export function Marquee({ text = "// EXECUTE   // INJECT   // DOMINATE", className = "" }) {
  const trackRef = useRef(null);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    let lastY = window.scrollY;
    let lastT = performance.now();
    let settle;
    const onScroll = () => {
      const now = performance.now();
      const y = window.scrollY;
      const velocity = Math.abs(y - lastY) / Math.max(now - lastT, 1);
      lastY = y;
      lastT = now;
      const duration = Math.max(5.5, Math.min(28, 28 - velocity * 14));
      track.style.animationDuration = `${duration}s`;
      clearTimeout(settle);
      settle = setTimeout(() => { track.style.animationDuration = "28s"; }, 180);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(settle);
    };
  }, []);

  const item = (
    <span className="font-display uppercase text-5xl sm:text-7xl font-medium tracking-tight px-8 text-white/90">
      {text}
    </span>
  );
  return (
    <div className={`velocity-marquee relative overflow-hidden border-y border-white/10 py-6 ${className}`} data-testid="velocity-marquee">
      <div ref={trackRef} className="flex w-max animate-marquee whitespace-nowrap" style={{ animationDuration: "28s" }}>
        {[...Array(6)].map((_, i) => <span key={i} className="flex items-center">{item}<span className="text-white/20 text-5xl sm:text-7xl">/</span></span>)}
      </div>
    </div>
  );
}

export function Logo({ size = 40, withText = true }) {
  return (
    <div className="flex items-center gap-3">
      <img src="/assets/logo-hero.png" alt="GZ" style={{ height: size, width: size }}
        className="object-contain" data-testid="logo-mark" />
      {withText && (
        <span className="font-anton text-xl tracking-[0.35em] uppercase leading-none text-white">GZ</span>
      )}
    </div>
  );
}

function countdownLabel(endsAt) {
  const ms = new Date(endsAt).getTime() - Date.now();
  if (isNaN(ms) || ms <= 0) return null;
  const s = Math.floor(ms / 1000);
  const pad = (n) => String(n).padStart(2, "0");
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return d > 0 ? `${d}D ${pad(h)}:${pad(m)}:${pad(sec)}` : `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

export function SaleCountdown({ endsAt, tid = "x", className = "" }) {
  const [label, setLabel] = useState(() => countdownLabel(endsAt));
  useEffect(() => {
    const id = setInterval(() => setLabel(countdownLabel(endsAt)), 1000);
    return () => clearInterval(id);
  }, [endsAt]);
  if (!label) return null;
  return (
    <span data-testid={`sale-countdown-${tid}`}
      className={`inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] text-white/70 border border-white/20 px-2 py-1 ${className}`}>
      <Timer size={11} className="shrink-0" /> ENDS IN {label}
    </span>
  );
}
